// deps
import { useCallback, useContext, useMemo, useState } from "react";

// internal-deps
import { DELIVERY_SECTION_ELEMENT, getBridgeSdk } from "@swiggy-private/checkout-core";
import { VibrateAmplitudeTypes } from "@swiggy-private/native-bridge";
import { scrollElementIntoView } from "@swiggy-private/core-ui";

// contexts
import { DeliverySectionContextValue } from "@cart-client/contexts/DeliverySectionContext";
import { CartContext } from "@cart-client/contexts/CartContext";

// helpers
import { isFooterCroutonExists } from "@cart-client/helpers/CartRendering";
import { isItemExceededLimitMessageExists } from "@cart-client/helpers/CartItemRenderer";
import { getDeliveryInstructionCroutonData } from "@cart-client/helpers/Cart";

export const useDeliverySectionContext = (canShowTimer: boolean): DeliverySectionContextValue => {
    const [selectedOption, setSelectedOption] = useState<DELIVERY_SECTION_ELEMENT | null>(null);
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;

    const onTabChange = useCallback((selectedOption: DELIVERY_SECTION_ELEMENT) => {
        // haptic feedback on tab change
        getBridgeSdk()
            .vibrate({ duration: 30, amplitude: VibrateAmplitudeTypes.LIGHT })
            .catch(() => void 0);

        setSelectedOption(selectedOption);
    }, []);

    const setDeliveryTab = useCallback((selectedOption: DELIVERY_SECTION_ELEMENT, shouldScrollToTab = true) => {
        if (shouldScrollToTab) {
            scrollElementIntoView(`#${selectedOption}`, false);
        }

        setSelectedOption(selectedOption);
    }, []);

    // Delivery Mode info
    const [isDeliverySpeedCroutonActive, setDeliverySpeedCroutonActive] = useState(false);

    const [isFooterCroutonPresent, isItemExceededLimitMessagePresent] = useMemo(
        () => [isFooterCroutonExists(cartResponse), isItemExceededLimitMessageExists(cartResponse)],
        [cartResponse],
    );

    const canShowDeliverySpeedCrouton = useMemo(
        () => !canShowTimer && !isItemExceededLimitMessagePresent && !isFooterCroutonPresent,
        [canShowTimer, isFooterCroutonPresent, isItemExceededLimitMessagePresent],
    );

    // Delivery Instructions Info
    const deliveryInstructionCroutonDetail = useMemo(() => getDeliveryInstructionCroutonData(cartResponse), [
        cartResponse,
    ]);

    /**
     * DI Crouton has least priority. So check if either cart timer or normal footer crouton or delivery speeed crouton is present. If not, then use the DI crouton.
     */
    const canShowDeliveryInstructionCrouton = useMemo(
        () =>
            !canShowTimer &&
            !isItemExceededLimitMessagePresent &&
            !isFooterCroutonPresent &&
            !isDeliverySpeedCroutonActive &&
            !!deliveryInstructionCroutonDetail?.enable,
        [
            canShowTimer,
            isItemExceededLimitMessagePresent,
            isFooterCroutonPresent,
            isDeliverySpeedCroutonActive,
            deliveryInstructionCroutonDetail?.enable,
        ],
    );

    return {
        selectedOption,
        onTabChange,
        setDeliveryTab,
        deliveryModeInfo: {
            isDeliverySpeedCroutonActive,
            canShowDeliverySpeedCrouton,
            setDeliverySpeedCroutonActive,
        },
        deliveryInstructionsInfo: {
            canShowDeliveryInstructionCrouton,
            deliveryInstructionCroutonDetail,
        },
    };
};
